import { supabase, supabaseEnabled } from './supabase'

export type NoteRow = {
  id?: string
  event_slug: string
  content_html: string
  updated_at?: string
  created_at?: string
}

const LOCAL_KEY = (slug: string) => `nanza:notes:${slug}`

// Reads the latest note for a given event slug. Supabase is the source of
// truth when configured; localStorage acts as both an offline fallback and a
// warm cache so reloads don't flash an empty editor.
export async function loadNote(eventSlug: string): Promise<string> {
  if (supabaseEnabled && supabase) {
    const { data, error } = await supabase
      .from('notes')
      .select('content_html')
      .eq('event_slug', eventSlug)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (!error && data?.content_html != null) {
      localStorage.setItem(LOCAL_KEY(eventSlug), data.content_html)
      return data.content_html
    }
    if (error) console.warn('[notes] supabase load failed', error)
  }
  return localStorage.getItem(LOCAL_KEY(eventSlug)) || ''
}

// Upsert the current note for the event AND append a snapshot to
// note_revisions so nothing edited is ever lost. Each call from the editor
// is already debounced upstream, so one revision per save is acceptable.
export async function saveNote(
  eventSlug: string,
  contentHtml: string,
): Promise<void> {
  // Always write local first for instant feedback / offline-safe state.
  localStorage.setItem(LOCAL_KEY(eventSlug), contentHtml)
  if (!supabaseEnabled || !supabase) return

  const { error } = await supabase
    .from('notes')
    .upsert(
      {
        event_slug: eventSlug,
        content_html: contentHtml,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'event_slug' },
    )
  if (error) {
    console.warn('[notes] supabase upsert failed', error)
    return
  }

  const { error: revError } = await supabase.from('note_revisions').insert({
    event_slug: eventSlug,
    content_html: contentHtml,
  })
  if (revError) console.warn('[notes] revision insert failed', revError)
}
